// Boot. Find or make a room, connect, build the hole, and run the loop. This file is still the
// wiring: everything added here hands the scene what it needs (the world for the palette, the hole
// for ground lookups, the wind for the flag) and nothing here decides an outcome. `onMessage` is
// unchanged in shape — the same cases, with the extra calls the new scene wants.

import * as THREE from 'three';
import { sampleHeight, sampleSurface } from '@shared/course';
import { CLUBS, aimTo, puttPowerFor, simulateShot, windFor, windSeedFor, type Club } from '@shared/physics';
import type { ServerMsg, ShotRequest } from '@shared/protocol';
import { createWorld } from './scene/world';
import { buildHole, makeAimLine, makePowerArc, tickHole, updateAimLine, updatePathLine } from './scene/course';
import { createDirector } from './scene/camera';
import { drawCaddieBoard, drawScoreboard, faceCamera, makeBoard, placeBoards } from './scene/props';
import { Ball } from './scene/ball';
import { Hud } from './scene/ui';
import { carryChart, estimateFor, type CarryChart } from './estimate';
import { Input } from './input';
import { setupXR } from './xr';
import { setupAR } from './ar';
import { Net, createRoom } from './net';
import { ClientState, loadIdentity, playerName, saveIdentity } from './state';
import { registerWebMCP } from './webmcp';

const BALL_COLORS = [0xffffff, 0xffd166, 0x8ecae6, 0xf4a261, 0xb5e48c, 0xcdb4db];

async function boot() {
  const app = document.getElementById('app')!;
  const cs = new ClientState();
  const params = new URLSearchParams(location.search);
  let roomId = params.get('room') ?? '';
  if (!roomId) {
    const made = await createRoom(undefined, Number(params.get('holes')) || 3);
    roomId = made.id;
    const url = new URL(location.href);
    url.searchParams.set('room', roomId);
    history.replaceState(null, '', url.toString());
  }
  cs.roomId = roomId;

  const world = createWorld(app);
  const { scene, camera, controls, renderer } = world;
  // the trail and the landing puff are camera-facing; this is how they find the camera
  scene.userData.camera = camera;

  // Everything that IS the course sits on one stage — the hole, the balls, the aim line, the
  // boards — so the passthrough tabletop can shrink it and put it down on a table in one move.
  // The rig (the player) and the sky stay on the scene.
  const stage = new THREE.Group();
  stage.name = 'stage';
  stage.userData.camera = camera;
  scene.add(stage);

  const director = createDirector(world);
  let holeGroup: THREE.Group | null = null;
  let holeIndexBuilt = -1;
  const balls = new Map<string, Ball>();
  const aimLine = makeAimLine();
  stage.add(aimLine);
  const powerArc = makePowerArc(world.theme.accent);
  stage.add(powerArc);
  // VR has no DOM: the card and the caddie's line live on two boards by the tee
  const boards = { score: makeBoard(1.9, 1.1), caddie: makeBoard(1.7, 0.72) };
  boards.score.visible = boards.caddie.visible = false;
  stage.add(boards.score, boards.caddie);

  let playing = 0; // trajectories in flight
  let flying: Ball | null = null;
  let chart: CarryChart | null = null;
  let chartKey = '';

  const ballFor = (playerId: string, idx: number) => {
    let b = balls.get(playerId);
    if (!b) { b = new Ball(stage, BALL_COLORS[idx % BALL_COLORS.length]); balls.set(playerId, b); }
    if (cs.hole) b.hole = cs.hole;
    return b;
  };

  const net = new Net(roomId, playerName(), () => cs.me ?? loadIdentity(roomId), onMessage, (s) => hud.note(`connection: ${s}`));

  let lobbyDismissed = false;
  const overlayAct = (act: string, el: HTMLButtonElement) => {
    if (act === 'tee') { lobbyDismissed = true; hud.hideOverlay(); }
    if (act === 'bot') { net.send({ t: 'add_bot' }); el.textContent = 'Seating Dex…'; }
    if (act === 'copy') { navigator.clipboard?.writeText(location.href).then(() => { el.textContent = 'Copied'; }, () => { el.textContent = location.href; }); }
    if (act === 'new') { location.href = location.pathname; }
  };

  // a short settle after every landing, so a key held through the flight cannot fire the moment
  // the ball stops
  let landedAt = 0;
  const SETTLE_MS = 600;
  const input = new Input(
    (club, aimDeg, power) => sendShot({ club, aimDeg, power, spin: 0 }),
    () => cs.isMyTurn && playing === 0 && !hud.overlay && performance.now() - landedAt > SETTLE_MS,
    () => (cs.hole && cs.myPlayer ? Math.hypot(cs.myPlayer.pos.x - cs.hole.cup.x, cs.myPlayer.pos.z - cs.hole.cup.z) : null),
  );
  // the putt preview: the real path at the meter's power (dead weight while idle), so the break
  // and the stopping point are on the ground where the player is looking
  let preview: ReturnType<typeof simulateShot> | null = null;
  let previewKey = '';
  let previewAt = 0;

  const hud = new Hud({
    aimLeft: () => input.aimBy(-input.aimStep()),
    aimRight: () => input.aimBy(input.aimStep()),
    clubPrev: () => input.clubPrev(),
    clubNext: () => input.clubNext(),
    swingStart: () => input.swingStart(),
    swingEnd: () => input.swingEnd(),
    swingCancel: () => input.swingCancel(),
    askCaddie: () => net.send({ t: 'ask_caddie' }),
    addBot: () => net.send({ t: 'add_bot' }),
    copyLink: () => { navigator.clipboard?.writeText(location.href).then(() => hud.note('link copied')); },
    setClub: (club: Club) => input.setClub(club),
  });

  const xr = setupXR(world, (aimDeg, power, speedMps) => {
    if (!cs.isMyTurn || playing > 0) return;
    // on the table: not before the course is down, and not on the press that put it down
    if (ar.active() && (!ar.placed() || ar.placedAgoMs() < 1500)) return;
    // A putt is a slow stroke. 2.5 m/s of hand is a full putter meter, scaled to the putt in
    // front of you exactly as the desk meter is; the hand-off sent a 1 m/s tap as a 15 % swing.
    const p = input.club === 'putter' ? Math.max(0.03, Math.min(1, speedMps / 2.5) * input.puttScale()) : power;
    // on the table the course is turned to face the player: a swing's world direction is read in
    // course coordinates by taking the stage's yaw back out
    let aim = ar.active() ? aimDeg - ar.stageYawDeg() : aimDeg;
    while (aim > 180) aim -= 360; while (aim < -180) aim += 360;
    sendShot({ club: input.club, aimDeg: aim, power: p, spin: 0 });
  }, {
    onSessionChange: (active) => { boards.score.visible = boards.caddie.visible = active && !ar.active(); },
  });
  const ar = setupAR(world, stage, {
    onSessionChange: (active) => { if (active) { boards.score.visible = boards.caddie.visible = false; } },
    onPlaced: () => hud.note('the course is on the table — your shot'),
  });

  function sendShot(shot: ShotRequest) {
    net.send({ t: 'shot', shot });
  }

  function rebuildHole() {
    const hole = cs.hole;
    if (!hole) return;
    if (holeGroup) stage.remove(holeGroup);
    holeGroup = buildHole(hole, world);
    stage.add(holeGroup);
    holeIndexBuilt = hole.index;
    hud.setAccent(world.theme.accent);
    placeBoards(hole, boards.score, boards.caddie);
    chart = null;
    chartKey = '';
    for (const [id, b] of balls) {
      b.hole = hole;
      const p = cs.state?.players.find((q) => q.id === id);
      if (p) b.setPosition(p.pos.x, sampleHeight(hole, p.pos.x, p.pos.z), p.pos.z);
    }
    aimAtFlag();
    frameCameraOnMe(true);
  }

  function aimAtFlag() {
    const hole = cs.hole, me = cs.myPlayer;
    if (!hole || !me) return;
    input.aimDeg = aimTo(me.pos, hole.cup);
    const d = Math.hypot(me.pos.x - hole.cup.x, me.pos.z - hole.cup.z);
    // the putter on the green, or close in from the fringe; never from the rough at 19 m
    const onGreen = sampleSurface(hole, me.pos.x, me.pos.z) === 'green';
    if (onGreen || d < 12) input.setClub('putter');
    else if (me.strokes[hole.index] === 0) input.setClub('driver');
    else {
      // the caddie's rule of thumb picks by distance — on a desk too, where the rack used to keep
      // the driver for a 68 m approach (QC round 2); the rack is still there to overrule it
      input.setClub(d < 60 ? 'wedge' : d < 110 ? 'iron9' : d < 150 ? 'iron7' : d < 190 ? 'iron5' : d < 230 ? 'wood' : 'driver');
    }
  }

  function frameCameraOnMe(instant = false) {
    const hole = cs.hole, me = cs.myPlayer;
    if (!hole || !me) return;
    if (ar.active()) return;   // on the table the player stands where they are
    if (xr.active()) { xr.standAt(me.pos.x, sampleHeight(hole, me.pos.x, me.pos.z), me.pos.z, input.aimDeg); return; }
    director.address(hole, me.pos, input.aimDeg, instant);
  }

  /** One chart per lie: 28 shared-physics runs, then interpolation while the meter moves. */
  function refreshChart() {
    const hole = cs.hole, me = cs.myPlayer;
    if (!hole || !me) { chart = null; return; }
    const stroke = me.strokes[hole.index] ?? 0;
    const key = `${hole.index}:${stroke}:${me.pos.x.toFixed(2)}:${me.pos.z.toFixed(2)}`;
    if (key === chartKey) return;
    chartKey = key;
    chart = carryChart(hole, me.pos, stroke);
  }

  function refreshHud() {
    const st = cs.state;
    if (!st) return;
    const myId = cs.me?.id ?? null;
    hud.setRoom(st.id, st.name, st.status);
    hud.setHole(st, myId, cs.hole);
    hud.setScores(st, myId);
    const hole = cs.hole, me = cs.myPlayer;
    const d = hole && me ? Math.hypot(me.pos.x - hole.cup.x, me.pos.z - hole.cup.z) : null;
    hud.setShot(input.club, input.aimDeg, cs.isMyTurn && playing === 0, d, hole && me ? aimTo(me.pos, hole.cup) : null);
    if (hole && me) hud.setWind(cs.isMyTurn ? st.wind : windFor(windSeedFor(hole, me.strokes[hole.index] ?? 0)));
    hud.setEstimate(input.club === 'putter' && preview ? preview.totalM : estimateFor(chart, input.club, input.charging ? input.shotPower() : 1));
    drawScoreboard(boards.score, st, world.theme.accent);
  }

  function onMessage(msg: ServerMsg) {
    switch (msg.t) {
      case 'welcome':
        cs.me = msg.you;
        saveIdentity(roomId, msg.you);
        cs.apply(msg.state);
        rebuildHole();
        refreshChart();
        refreshHud();
        hud.note(`you are in ${msg.state.name}`);
        break;
      case 'state': {
        const before = cs.state?.hole;
        const wasMyTurn = cs.isMyTurn;
        cs.apply(msg.state);
        // the lobby while the room is open (refreshed as players arrive), the card when it is finished
        if (cs.state?.status === 'open' && !lobbyDismissed) hud.showLobby(cs.state, cs.me?.id ?? null, overlayAct);
        else if (cs.state?.status === 'finished') { if (hud.overlay !== 'round') hud.showRoundCard(cs.state, cs.me?.id ?? null, overlayAct); }
        else if (hud.overlay === 'lobby') hud.hideOverlay();
        if (cs.state && cs.state.hole !== before) { rebuildHole(); hud.note(`hole ${cs.state.hole + 1}`); }
        else if (cs.hole && holeIndexBuilt !== cs.hole.index) rebuildHole();
        // players who did not move this frame still need their balls placed
        cs.state?.players.forEach((p, i) => {
          const b = ballFor(p.id, i);
          if (!b.playing && cs.hole) b.setPosition(p.pos.x, sampleHeight(cs.hole, p.pos.x, p.pos.z), p.pos.z);
          // back in view on a fresh hole (a holed ball hides itself until then)
          if (cs.state && cs.state.hole !== before && !b.playing) b.setVisible(true);
        });
        // only when the turn has just come to me: a state is also broadcast when a friend reloads
        // their tab, and that used to re-aim me at the flag and hand me the putter mid-address
        if (cs.isMyTurn && !wasMyTurn) { aimAtFlag(); frameCameraOnMe(); }
        refreshChart();
        refreshHud();
        break;
      }
      case 'shot_result': {
        const idx = cs.state?.players.findIndex((p) => p.id === msg.playerId) ?? 0;
        const b = ballFor(msg.playerId, idx);
        playing++;
        flying = b;
        if (cs.hole) director.follow(b, cs.hole);
        b.play(msg.result.samples, () => {
          playing--;
          landedAt = performance.now();
          if (flying === b) flying = null;
          // a holed ball is in the cup, not on it: out of sight until the next hole is built
          if (msg.result.holed) b.setVisible(false);
          refreshHud();
          if (cs.isMyTurn) frameCameraOnMe();
        });
        const who = cs.state?.players.find((p) => p.id === msg.playerId)?.name ?? msg.playerId;
        hud.note(`${who}: ${CLUBS[msg.shot.club].label}, ${msg.shot.club === 'putter' ? msg.result.totalM + ' m roll' : msg.result.carryM + ' m carry'}, ${msg.result.surface}${msg.result.holed ? ', in' : ''}${msg.result.penalty ? ', penalty' : ''}`);
        break;
      }
      case 'caddie': {
        const who = msg.kind === 'bot' ? 'Dex plays' : 'Dex';
        hud.say(msg.text, msg.source, who);
        hud.note(`Dex: ${msg.text}`);
        drawCaddieBoard(boards.caddie, msg.text, who, msg.source, world.theme.accent);
        break;
      }
      case 'chat':
        hud.note(`${msg.name}: ${msg.text}`);
        break;
      case 'hole_done':
        hud.note(`hole ${msg.hole + 1} done`);
        break;
      case 'error':
        hud.note(`error: ${msg.message}`);
        // never seated (the round is over, the room is full, the address is wrong): a card with a
        // way onward, not a line in the tape under an empty HUD
        if (!cs.me && /over|full|no such room|not initialised/.test(msg.message)) hud.showNotice(msg.message, overlayAct);
        break;
    }
  }

  registerWebMCP({
    state: () => cs.state,
    takeShot: async (args) => { if (!cs.isMyTurn) return { error: 'not your turn' }; sendShot({ club: args.club as Club, aimDeg: args.aimDeg, power: args.power, spin: args.spin ?? 0 }); return { ok: true }; },
    askCaddie: async () => { net.send({ t: 'ask_caddie' }); return { ok: true }; },
  });

  /* a test seam, like __pt on the puzzle engine and __fs on the mahjong table: the XR witness
     (scripts/xr-witness.mjs) drives a fake headset through it. Reads only; nothing here decides. */
  (window as unknown as { __linksApp: unknown }).__linksApp = {
    world, cs, net, input, xr, ar, stage, hud, boards, balls, THREE,
    get playing() { return playing; },
  };

  net.connect();

  let last = performance.now();
  renderer.setAnimationLoop(() => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const t = now / 1000;

    input.update(dt);
    xr.update();
    ar.update();
    for (const b of balls.values()) b.update();
    if (holeGroup) tickHole(holeGroup, t, world, cs.state?.wind);

    const hole = cs.hole, me = cs.myPlayer;
    const addressing = !!hole && !!me && cs.isMyTurn && playing === 0;
    if (hole && me) {
      if (addressing && input.club === 'putter') {
        const now = performance.now();
        const key = `${input.aimDeg.toFixed(2)}|${input.charging ? input.shotPower().toFixed(3) : 'idle'}|${me.pos.x.toFixed(2)}|${me.pos.z.toFixed(2)}`;
        if (previewKey !== key && now - previewAt > 100) {
          const d = Math.hypot(me.pos.x - hole.cup.x, me.pos.z - hole.cup.z);
          const power = input.charging ? input.shotPower() : puttPowerFor(d);
          preview = simulateShot(hole, { club: 'putter', aimDeg: input.aimDeg, power, spin: 0, from: me.pos, windSeed: windSeedFor(hole, me.strokes[hole.index] ?? 0) });
          previewKey = key; previewAt = now;
          updatePathLine(aimLine, hole, preview.samples, preview.holed ? '#8fd35f' : world.theme.accent);
        }
      } else {
        preview = null;
        const reach = CLUBS[input.club].speed * 2.4;
        updateAimLine(aimLine, hole, me.pos, input.aimDeg, addressing ? reach : 0.01, world.theme.accent);
      }
      aimLine.visible = addressing;
      if (addressing) {
        powerArc.visible = input.charging;
        powerArc.position.set(me.pos.x, sampleHeight(hole, me.pos.x, me.pos.z) + 0.03, me.pos.z);
        powerArc.userData.set(input.power, input.aimDeg);
      } else {
        powerArc.visible = false;
      }
    }
    if (input.charging || cs.isMyTurn) hud.setPower(input.power);

    if (!xr.active()) {
      if (flying && flying.playing && hole && director.mode === 'follow') director.tickFollow(flying, hole);
      director.update();
    }
    if (boards.score.visible) { faceCamera(boards.score, camera); faceCamera(boards.caddie, camera); }
    renderer.render(scene, camera);
  });

  // keep the aim, the estimate and the meter's readout live while keys are held
  setInterval(refreshHud, 250);
}

boot().catch((e) => {
  const hud = document.getElementById('hud-caddie-text') ?? document.getElementById('hud-caddie');
  if (hud) hud.textContent = `could not start: ${e instanceof Error ? e.message : String(e)}`;
  console.error(e);
});
